import type { SupabaseClient } from '@supabase/supabase-js'
import type { Cuenta, Database } from '@/types/database'

type CuentaInsert = Database['public']['Tables']['cuentas']['Insert']
type CuentaUpdate = Database['public']['Tables']['cuentas']['Update']

// Cuenta con su saldo. Antes salía de vista_saldos_cuentas; desde ORR-613 se arma con las columnas
// de la cuenta y los agregados de la RPC saldos_cuentas.
export interface CuentaConSaldo {
  id: string  // Siempre existe en registros de la BD
  organizacion_id: string | null  // Campo crítico para multi-tenancy
  nombre: string | null
  tipo: string | null
  institucion: string | null
  moneda: string | null
  cbu_cvu_alias: string | null
  activa: boolean | null
  saldo_inicial: number | null
  saldo_actual: number | null
  total_movimientos: number | null
  ultimo_movimiento: string | null
  cliente_id: string | null
  descripcion: string | null
  observaciones: string | null
  created_at: string | null
}

/**
 * Filtro PostgREST de organización para cuentas, para usar con `query.or(...)`.
 * Incluye las cuentas con `organizacion_id` NULL, que son compartidas por todas las organizaciones.
 * Un `.eq('organizacion_id', X)` las excluye, porque en SQL `NULL = X` nunca es verdadero.
 */
export const cuentaOrgFilter = (organizacionId: string): string =>
  `organizacion_id.eq.${organizacionId},organizacion_id.is.null`

// Fila de la RPC saldos_cuentas: agregados de los movimientos de la organización en una cuenta.
interface SaldoCuenta {
  cuenta_id: string
  total_movimientos: number
  ultimo_movimiento: string | null
  total_debe: number
  total_haber: number
}

const COLUMNAS_CUENTA =
  'id, organizacion_id, nombre, tipo, institucion, moneda, cbu_cvu_alias, activa, saldo_inicial, cliente_id, descripcion, observaciones, created_at'

/**
 * ORR-613: agregados por cuenta de todos los movimientos de la organización, sin el Gasto pendiente.
 * La RPC exige verCuentas y no verMovimientos. vista_saldos_cuentas suma después de la RLS de
 * movimientos: sin verMovimientos omite los cobros y da un saldo falso. Si la RPC falla se lanza el
 * error, para no mostrar un saldo incompleto.
 */
const saldosPorCuenta = async (
  client: SupabaseClient<Database>,
  organizacionId: string,
): Promise<Map<string, SaldoCuenta>> => {
  // La RPC no está en database.ts. El cast conserva `this` del cliente.
  const { data, error } = await (client as unknown as {
    rpc: (fn: string, args: Record<string, unknown>) => Promise<{ data: unknown; error: unknown }>
  }).rpc('saldos_cuentas', { p_org: organizacionId })
  if (error) throw error
  return new Map(((data ?? []) as SaldoCuenta[]).map((saldo) => [saldo.cuenta_id, saldo]))
}

/**
 * Servicio para gestión de cuentas.
 *
 * IMPORTANTE: Todos los métodos reciben el cliente Supabase autenticado como primer parámetro.
 * Usar con useSupabase() desde el hook que llame a estos métodos.
 *
 * @example
 * const { supabase } = useSupabase()
 * const cuentas = await cuentasService.listarConSaldo(supabase!, organizacionId)
 */
export const cuentasService = {
  async crear(client: SupabaseClient<Database>, cuenta: CuentaInsert): Promise<Cuenta> {
    const { data, error } = await client
      .from('cuentas')
      .insert(cuenta as never)
      .select()
      .single()

    if (error) throw error
    return data as Cuenta
  },

  async actualizar(client: SupabaseClient<Database>, id: string, cuenta: CuentaUpdate): Promise<Cuenta> {
    const { data, error } = await client
      .from('cuentas')
      .update(cuenta as never)
      .eq('id', id)
      .select()
      .single()

    if (error) throw error
    return data as Cuenta
  },

  async eliminar(client: SupabaseClient<Database>, id: string): Promise<void> {
    const { error } = await client
      .from('cuentas')
      .delete()
      .eq('id', id)

    if (error) throw error
  },

  async toggleActiva(client: SupabaseClient<Database>, id: string, activa: boolean): Promise<Cuenta> {
    return this.actualizar(client, id, { activa })
  },

  // Obtener estadísticas de uso de una cuenta
  async obtenerEstadisticas(
    client: SupabaseClient<Database>,
    cuentaId: string,
    organizacionId: string
  ): Promise<{ totalMovimientos: number; saldoActual: number; totalDebe: number; totalHaber: number }> {
    // Obtener saldo inicial de la cuenta
    const { data: cuenta, error: errorCuenta } = await client
      .from('cuentas')
      .select('saldo_inicial')
      .eq('id', cuentaId)
      .single()

    if (errorCuenta) throw errorCuenta

    // Los totales salen de saldos_cuentas, con el mismo criterio que el saldo de la lista: sin los
    // gastos PENDIENTES (ORR-124, decisión 9) y sin depender de verMovimientos (ORR-613).
    const saldo = (await saldosPorCuenta(client, organizacionId)).get(cuentaId)
    const saldoInicial = (cuenta as { saldo_inicial: number | null })?.saldo_inicial || 0
    const totalDebe = Number(saldo?.total_debe ?? 0)
    const totalHaber = Number(saldo?.total_haber ?? 0)

    return {
      totalMovimientos: Number(saldo?.total_movimientos ?? 0),
      totalDebe,
      totalHaber,
      saldoActual: saldoInicial + totalHaber - totalDebe,
    }
  },

  // Obtener cuentas con saldos calculados
  async listarConSaldo(
    client: SupabaseClient<Database>,
    organizacionId: string,
    incluirInactivas = false
  ): Promise<CuentaConSaldo[]> {
    let query = client
      .from('cuentas')
      .select(COLUMNAS_CUENTA)
      .or(cuentaOrgFilter(organizacionId))
      .order('nombre')

    if (!incluirInactivas) {
      query = query.eq('activa', true)
    }

    const [{ data, error }, saldos] = await Promise.all([query, saldosPorCuenta(client, organizacionId)])

    if (error) throw error
    return ((data ?? []) as unknown as Omit<CuentaConSaldo, 'saldo_actual' | 'total_movimientos' | 'ultimo_movimiento'>[])
      .map((cuenta) => {
        const saldo = saldos.get(cuenta.id)
        return {
          ...cuenta,
          saldo_actual:
            (cuenta.saldo_inicial ?? 0) + Number(saldo?.total_haber ?? 0) - Number(saldo?.total_debe ?? 0),
          total_movimientos: Number(saldo?.total_movimientos ?? 0),
          ultimo_movimiento: saldo?.ultimo_movimiento ?? null,
        }
      })
  },
}
